import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Icon, IconProps } from '../Icon'
import { FontAwesomeIcon } from '../types/css'
import { CSSResize } from '../types/css/rules'
import { BaseProps, BorderProps } from '../types/props'
import { ComponentError } from '../util/components'
import { getBorderStyle } from '../util/props/border'
import { overloadColor } from '../util/style'

interface WrapperProps {
  width: string
}
const Wrapper = styled.div<WrapperProps>`
  position: relative;
  width: ${props => props.width};
`
interface StyledTextAreaProps extends BorderProps {
  resize: CSSResize
  height: string
  fontSize: string
  color: string
  background: string
  hasHint: boolean
}
const StyledTextArea = styled.textarea<StyledTextAreaProps>`
  width: 100%;
  height: ${props => props.height};
  padding: 6px;
  padding-bottom: ${props => props.hasHint && '1rem'};

  resize: ${props => props.resize};
  outline: none;

  font-size: ${props => props.fontSize};
  font-family: inherit;
  color: ${props => overloadColor(props.color)};
  background: ${props => overloadColor(props.background)};

  ${getBorderStyle}
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};

  transition: border 0.3s ease-in-out;

  &:focus {
    border: 1px solid ${props => props.theme.colors.primary};
  }
`
const Submit = styled.div`
  position: absolute;
  z-index: 1;
  left: 12px;
  right: 12px;
  bottom: 12px;

  display: flex;
  justify-content: space-between;

  span {
    color: ${props => props.theme.colors.gray5};
    font-size: 0.8rem;
    transform: translate(-7px, 6px);
  }
`

export interface TextAreaSubmitConfig {
  icon?: FontAwesomeIcon | IconProps
  /**
   * Whether to fire onSubmit on Ctrl+Enter
   * @default true
   */
  ctrlEnter?: boolean
  hide?: boolean
  hint?: string
}

export interface TextAreaProps extends BorderProps, BaseProps {
  value: string

  error?: string | null
  errorTimeout?: number
  placeholder?: string

  disabled?: boolean
  autoGrowing?: boolean
  autoFocus?: boolean

  /** Configure submit behaviour and appearance. Will do nothing if onSubmit is not defined. */
  submitConfig?: TextAreaSubmitConfig

  height?: string
  width?: string
  fullWidth?: boolean
  rows?: number
  resize?: CSSResize
  fontSize?: string
  color?: string
  background?: string

  onErrorTimeout?(): void
  onClick?(event: React.MouseEvent<HTMLTextAreaElement>): void
  onKeyDown?(event: React.KeyboardEvent<HTMLTextAreaElement>): void
  onCtrlEnter?(event: React.KeyboardEvent<HTMLTextAreaElement>): void
  onFocus?(event: React.FocusEvent<HTMLTextAreaElement>): void
  onBlur?(event: React.FocusEvent<HTMLTextAreaElement>): void
  /** If not undefined, a submit icon will appear in lower right */
  onSubmit?(value: string): void
  onChange(value: string, event: React.ChangeEvent<HTMLTextAreaElement>): void
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className,
      id,
      value,
      error,
      errorTimeout,
      placeholder,

      disabled = false,
      autoGrowing = false,
      autoFocus = false,

      submitConfig,

      height = '122px',
      width = '300px',
      fullWidth = false,
      rows,
      resize = 'vertical',

      border,
      borderWidth,
      borderStyle,
      borderColor,
      fontSize = '1em',
      color = 'text.dark',
      background = 'white',

      onErrorTimeout,
      onClick,
      onFocus,
      onBlur,
      onKeyDown,
      onCtrlEnter,
      onSubmit,
      onChange,
    },
    ref
  ) => {
    const localRef = useRef<HTMLTextAreaElement | null>(null)
    const getElement = useCallback(
      () => (ref && 'current' in ref ? ref.current : localRef?.current),
      [ref]
    )

    const theme = useTheme()

    const [dynamicHeight, setDynamicHeight] = useState<string | null>(null)
    const [internalError, setInternalError] = useState(error)

    useEffect(() => {
      setInternalError(error)
    }, [error])

    const autoGrow = useCallback(() => {
      if (!autoGrowing) return

      const el = getElement()
      if (!el) return

      const height = el.clientHeight
      const scrollHeight = el.scrollHeight

      if (scrollHeight > height) setDynamicHeight(scrollHeight + 6 + 'px')
    }, [autoGrowing, getElement])

    const parsedBorder = useMemo(
      () => border ?? theme.layout.defaultBorder,
      [border, theme.layout.defaultBorder]
    )

    const submitIcon = useMemo<IconProps>(
      () =>
        !submitConfig?.icon
          ? {
              icon: 'paper-plane',
              type: 'solid',
              color: 'primary',
              cursor: 'pointer',
            }
          : typeof submitConfig.icon === 'string'
          ? {
              icon: submitConfig.icon,
            }
          : submitConfig.icon,
      [submitConfig]
    )

    function handleKeyDown(evt: React.KeyboardEvent<HTMLTextAreaElement>) {
      onKeyDown?.(evt)
      if (evt.defaultPrevented) return

      if (evt.ctrlKey && evt.key === 'Enter') {
        onCtrlEnter?.(evt)
        if (evt.defaultPrevented) return

        if (submitConfig?.ctrlEnter !== false) onSubmit?.(value)
      }
    }

    function handleErrorTimeout() {
      setInternalError(null)
      onErrorTimeout?.()
    }

    function handleSubmit(evt: React.MouseEvent<HTMLElement>) {
      if (typeof submitIcon === 'object') {
        submitIcon.onClick?.(evt)
        if (evt.defaultPrevented) return
      }

      onSubmit?.(value)
    }

    function handleChange(evt: React.ChangeEvent<HTMLTextAreaElement>) {
      onChange(evt.target.value, evt)
      autoGrow()
    }

    return (
      <Wrapper className={className} id={id} width={fullWidth ? '100%' : width}>
        <ComponentError
          className="--text-area-error"
          error={internalError}
          timeout={errorTimeout}
          onTimeout={handleErrorTimeout}
        />

        <StyledTextArea
          ref={ref ?? localRef}
          value={value}
          rows={rows}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          height={dynamicHeight ?? height}
          resize={resize}
          fontSize={fontSize}
          border={parsedBorder}
          borderWidth={borderWidth}
          borderStyle={borderStyle}
          borderColor={borderColor}
          color={color}
          background={background}
          hasHint={!!submitConfig?.hint}
          onClick={onClick}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
        />

        {typeof onSubmit !== 'undefined' && submitConfig?.hide !== true && (
          <Submit>
            {submitConfig?.hint && (
              <span className="--submit-hint">{submitConfig?.hint}</span>
            )}

            <Icon {...submitIcon} onClick={handleSubmit} />
          </Submit>
        )}
      </Wrapper>
    )
  }
)
